import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Check, ArrowRight, ArrowLeft, Sparkles, Building2, Hospital, Mail } from "lucide-react";
import NdaDownloadGate from "@/components/NdaDownloadGate";

/* /pricing — transparent B2B pricing.
 *
 * Three tiers (Pilot · Standard · Enterprise) using ranges, not exact
 * numbers, so the founder isn't pinned to a published rate during early
 * conversations. Every CTA routes to a mailto so prospects can self-serve
 * starting the conversation. The Calendly "Org Discovery Call" link is
 * surfaced prominently — it's the highest-intent action on this page.
 */
const PRICING_MAIL_TO = "ashlee@asvmedicalservices.com";
const ORG_DISCOVERY_CALENDLY =
    process.env.REACT_APP_CALENDLY_ORG_DISCOVERY_URL ||
    "https://calendly.com/ashlee-asvmedicalservices/30min";

function pricingMailto(subject, body) {
    return `mailto:${PRICING_MAIL_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const TIERS = [
    {
        id: "pilot",
        name: "Pilot",
        tagline: "Try Memory Lane with your families, risk-free.",
        price: "Free for 90 days",
        priceSub: "Then convert or walk away — no auto-renew.",
        accent: "sage",
        icon: Sparkles,
        bullets: [
            "Up to 25 caregivers",
            "Full caregiver feature set (Lane AI, Wellness, Documents, Behavior, Care-Team summaries)",
            "Aggregate, de-identified org admin dashboard",
            "Co-brand pack (logo, colors, tagline)",
            "Mutual NDA + 30-min implementation call",
            "Email support",
        ],
        cta: "Start a pilot",
        ctaMailto: pricingMailto(
            "Pilot enquiry — Memory Lane Care Navigator",
            "Hi Ashlee,\n\nWe'd like to start a 90-day pilot with our families.\n\n- Organization name:\n- Type (Agency / Care provider / GUIDE / Hospice / Clinic / Health system):\n- Approximate number of family caregivers:\n- Primary contact (name, role, email, phone):\n- Ideal pilot start date:\n\nThanks,"
        ),
    },
    {
        id: "standard",
        name: "Standard",
        tagline: "For care providers ready to scale Memory Lane to their full caseload.",
        price: "From $4–8 / caregiver / month",
        priceSub: "Or a flat $X / month for unlimited caregivers — billed annually.",
        accent: "teal",
        icon: Building2,
        featured: true,
        bullets: [
            "Up to 250 caregivers (then flat-rate)",
            "Everything in Pilot",
            "CSV / PDF export of cohort analytics",
            "Email alerts when caregiver hits a burnout threshold",
            "White-label printable family handout PDF",
            "Quarterly executive PDF auto-emailed to the org admin",
            "Quarterly business review with Ashlee",
            "Priority email + chat support",
        ],
        cta: "Talk pricing with Ashlee",
        ctaMailto: pricingMailto(
            "Standard plan — Memory Lane",
            "Hi Ashlee,\n\nWe're interested in the Standard plan. A bit about us:\n\n- Organization name:\n- Approximate caregiver count:\n- Timeline:\n\nCan we book a 30-min discovery call?\n\nThanks,"
        ),
    },
    {
        id: "enterprise",
        name: "Enterprise",
        tagline: "Health systems, payers, and multi-site agencies.",
        price: "Custom",
        priceSub: "Built around your contracting, security, and rollout requirements.",
        accent: "yellow",
        icon: Hospital,
        bullets: [
            "Unlimited caregivers across multiple sub-orgs",
            "Everything in Standard",
            "BAA available · SOC 2 readiness package",
            "SSO (Google / Microsoft) · SCIM provisioning (roadmap)",
            "Dedicated implementation lead",
            "Custom contracting, data residency, and reporting cadence",
            "Annual on-site or virtual training for your team",
        ],
        cta: "Request an enterprise quote",
        ctaMailto: pricingMailto(
            "Enterprise enquiry — Memory Lane",
            "Hi Ashlee,\n\nWe're a [health system / payer / multi-site agency] looking at enterprise terms.\n\n- Organization:\n- Approximate caregiver / family volume:\n- Key requirements (BAA, SSO, data residency, reporting):\n- Timeline:\n\nWho's the best person on your side to start with?\n\nThanks,"
        ),
    },
];

const ACCENT = {
    sage:   { card: "bg-sage-light/70 border-sage-soft",   pill: "bg-sage text-white",   icon: "bg-sage text-white",   cta: "bg-sage-dark hover:bg-sage" },
    teal:   { card: "bg-teal-soft/60 border-teal-light ring-2 ring-teal-light", pill: "bg-teal-dark text-white", icon: "bg-teal-dark text-white", cta: "bg-teal-dark hover:bg-teal" },
    yellow: { card: "bg-yellow-light border-yellow",       pill: "bg-yellow-dark text-white", icon: "bg-yellow-dark text-white", cta: "bg-yellow-dark hover:bg-yellow text-white" },
};

const FAQ = [
    {
        q: "How is a caregiver counted?",
        a: "One caregiver = one human who redeems your invite code and signs in at least once during the billing month. Inactive accounts don't count, family-shared accounts count once.",
    },
    {
        q: "Do you charge per patient or per caregiver?",
        a: "Per caregiver. Many patients have 2–3 caregivers, and we believe everyone in a care circle deserves their own login. You only pay for caregivers who actually use Memory Lane in the month.",
    },
    {
        q: "What does the pilot actually cost?",
        a: "Zero dollars for 90 days, up to 25 caregivers. No credit card needed. At day 90 we'll send you the data we'd both review — engagement, wellness improvement, behavior themes — and you decide whether to convert.",
    },
    {
        q: "Is Memory Lane HIPAA-compliant?",
        a: "Today we're not a HIPAA-covered entity by design — Memory Lane is a caregiver-facing journal, not a medical record. See /security for the architectural decisions behind that, plus our roadmap (HIPAA controls Q2 2026, SOC 2 Type 1 Q4 2026). Enterprise tier includes a BAA the moment that's done.",
    },
    {
        q: "Can I co-brand without an enterprise contract?",
        a: "Yes. Co-brand (logo, primary color, tagline, landing blurb) is built-in from the Pilot tier. Most B2B SaaS makes you buy Enterprise for branding — we don't.",
    },
    {
        q: "What about an annual commitment?",
        a: "Annual prepay knocks ~15% off Standard pricing and is required at Enterprise. Pilots are month-to-month while we figure out fit together.",
    },
];

export default function Pricing() {
    return (
        <main className="min-h-screen bg-cream">
            <SeoHead
                title="Memory Lane Care — Pricing & Plans for Families and Partners"
                description="Memory Lane is free to caregivers during beta. See pricing for families and partner organizations — neurology clinics, GUIDE participating providers, home health, hospice, and senior-living groups."
                canonicalPath="/pricing"
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6" data-testid="pricing-back">
                    <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
                </Link>

                <header className="text-center max-w-3xl mx-auto mb-10">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">Pricing</p>
                    <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Honest pricing for care providers and agencies.
                    </h1>
                    <p className="mt-4 text-lg text-[#4C5551] leading-relaxed">
                        Pilot free for 90 days. Convert when the data shows it&apos;s working — or walk away with everything you learned. No long contracts to pilot.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-3">
                        <a
                            href={ORG_DISCOVERY_CALENDLY}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="pricing-book-discovery"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal"
                        >
                            <ArrowRight size={14} /> Book a 30-min discovery call
                        </a>
                        <a
                            href={pricingMailto("Pricing question — Memory Lane", "Hi Ashlee,\n\nQuick question on pricing:\n\n")}
                            data-testid="pricing-email"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white border border-teal-light text-teal-dark text-sm font-medium hover:bg-teal-soft/40"
                        >
                            <Mail size={14} /> Email Ashlee
                        </a>
                    </div>
                </header>

                {/* Tiers */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-14" data-testid="pricing-tiers">
                    {TIERS.map(t => {
                        const A = ACCENT[t.accent] || ACCENT.teal;
                        const Icon = t.icon;
                        return (
                            <article
                                key={t.id}
                                data-testid={`pricing-tier-${t.id}`}
                                className={`ml-card p-5 md:p-6 relative border ${A.card}`}
                            >
                                {t.featured && (
                                    <span className={`absolute -top-3 right-5 inline-flex items-center gap-1 px-3 py-0.5 rounded-2xl text-xs font-semibold ${A.pill}`}>
                                        Most popular
                                    </span>
                                )}
                                <div className="flex items-center gap-3 mb-3">
                                    <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${A.icon}`}>
                                        <Icon size={20} strokeWidth={1.7} />
                                    </span>
                                    <h2 className="text-2xl font-heading font-medium text-[#12776F]">{t.name}</h2>
                                </div>
                                <p className="text-sm text-[#4C5551] mb-3 min-h-[40px]">{t.tagline}</p>
                                <p className="text-2xl font-heading font-semibold text-[#12776F]" data-testid={`pricing-tier-${t.id}-price`}>
                                    {t.price}
                                </p>
                                <p className="text-xs text-[#6E7A74] mb-4 min-h-[32px]">{t.priceSub}</p>

                                <ul className="space-y-1.5 text-sm text-[#1F2624] mb-5">
                                    {t.bullets.map(b => (
                                        <li key={b} className="flex items-start gap-2">
                                            <Check size={14} strokeWidth={2.4} className="text-teal-dark mt-0.5 shrink-0" /> {b}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={t.ctaMailto}
                                    data-testid={`pricing-tier-${t.id}-cta`}
                                    className={`w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl text-white text-sm font-medium transition-colors ${A.cta}`}
                                >
                                    {t.cta} <ArrowRight size={14} />
                                </a>
                            </article>
                        );
                    })}
                </section>

                {/* Trust strip — link to /security + NDA gate */}
                <section className="ml-card p-5 md:p-6 mb-12 bg-gradient-to-br from-teal-soft/30 via-cream to-yellow-light/30 border border-teal-light" data-testid="pricing-trust">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="max-w-xl">
                            <h3 className="text-xl font-heading font-medium text-[#1F2624]">Security &amp; legal, before we even talk price</h3>
                            <p className="text-sm text-[#4C5551] mt-1">
                                Read our security write-up or sign a mutual NDA before sharing anything proprietary on a call.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link to="/security" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-teal-dark text-white text-sm hover:bg-teal" data-testid="pricing-security-link">
                                Security &amp; privacy <ArrowRight size={13} />
                            </Link>
                            <NdaDownloadGate
                                testidPrefix="pricing"
                                trigger={
                                    <span data-testid="pricing-nda-download" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-white border border-teal-light text-teal-dark text-sm font-medium hover:bg-teal-soft/40 cursor-pointer">
                                        Download mutual NDA
                                    </span>
                                }
                            />
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="ml-card p-5 md:p-7 bg-white" data-testid="pricing-faq">
                    <h3 className="text-2xl md:text-3xl font-heading font-medium text-[#12776F] mb-4">FAQ</h3>
                    <div className="divide-y divide-cream-dark">
                        {FAQ.map((f, i) => (
                            <details key={i} className="group py-3" data-testid={`pricing-faq-${i}`}>
                                <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                                    <span className="font-medium text-[#1F2624]">{f.q}</span>
                                    <span className="text-teal-dark text-sm shrink-0 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                <p className="text-center text-xs text-[#6E7A74] mt-8">
                    Pricing ranges shown are indicative. Final terms confirmed in writing.
                </p>
            </div>
        </main>
    );
}
